import { useEffect } from 'react'
import { useUIStore } from '@/stores/uiStore'
import { useLayoutStore } from '@/stores/layoutStore'

export function useKeyboardShortcuts() {
  const setViewMode = useUIStore(s => s.setViewMode)
  const clearSelection = useUIStore(s => s.clearSelection)
  const selectedIds = useUIStore(s => s.selectedIds)
  const setFocusedZoneId = useUIStore(s => s.setFocusedZoneId)
  const deleteDevice = useLayoutStore(s => s.deleteDevice)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Ignore shortcuts when typing in input, textarea, or contenteditable
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase()
      if (
        tag === 'input' ||
        tag === 'textarea' ||
        (e.target as HTMLElement)?.isContentEditable
      ) {
        return
      }

      const isCtrlOrCmd = e.ctrlKey || e.metaKey
      const isAltOrShift = e.altKey || e.shiftKey

      // Ctrl+z / Cmd+z → Undo (placeholder)
      if (
        (e.ctrlKey || e.metaKey) &&
        e.key.toLowerCase() === 'z' &&
        !isAltOrShift
      ) {
        e.preventDefault()
        console.log('[Keyboard] Undo (not yet implemented)')
        return
      }

      // Ctrl+y / Cmd+y → Redo (placeholder)
      if (
        (e.ctrlKey || e.metaKey) &&
        e.key.toLowerCase() === 'y' &&
        !isAltOrShift
      ) {
        e.preventDefault()
        console.log('[Keyboard] Redo (not yet implemented)')
        return
      }

      // Ignore single-key shortcuts if any modifier keys are held (except ctrl+z/y handled above)
      if (isCtrlOrCmd || isAltOrShift) {
        return
      }

      const key = e.key.toLowerCase()

      // View switching: g, m, l
      if (key === 'g') {
        e.preventDefault()
        setViewMode('graph')
        return
      }

      if (key === 'm') {
        e.preventDefault()
        setViewMode('matrix')
        return
      }

      if (key === 'l') {
        e.preventDefault()
        setViewMode('list')
        return
      }

      // Escape → clear selection and focused zone
      if (key === 'escape') {
        e.preventDefault()
        clearSelection()
        setFocusedZoneId(null)
        return
      }

      // Delete or Backspace → delete all selected devices
      if (key === 'delete' || key === 'backspace') {
        e.preventDefault()
        selectedIds.forEach(id => deleteDevice(id))
        return
      }

      // f → focus first selected zone (for now, just log)
      if (key === 'f') {
        e.preventDefault()
        if (selectedIds.size === 1) {
          const firstId = Array.from(selectedIds)[0]
          console.log('[Keyboard] Focus zone:', firstId)
          // TODO: Wire zone focus logic here
        }
        return
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [setViewMode, clearSelection, selectedIds, setFocusedZoneId, deleteDevice])
}
